import React, { ReactElement, useEffect, useState } from "react";
import { List, ListItemText, Typography, Paper, Stack, Box, IconButton, Avatar, ListItemButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getUserById, imageIdToUrl, Recipe, User } from "../data";
import { getMonth } from "../dates";

export interface RecipeRevision {
    id: number;
    author_id: number;
    date: string;
}

function RecipeAuthor({ author }: { author: User | undefined }): ReactElement {
    if (!author) {
        return <div>Okänd användare</div>;
    }

    return (
        <Stack direction="row" alignItems="center" useFlexGap spacing="15px">
            <Avatar
                alt={author.name}
                src={imageIdToUrl("thumb", author.avatar_image_id)}
                sx={{ width: 30, height: 30 }}
            />
            <div>{author.name}</div>
        </Stack>
    );
}

export interface RecipeRevisionsProps {
    currentRevisionId?: number;
    revisions: Recipe[];
    onSelect?: (revision: Recipe) => void;
    onClose?: () => void;
}

export default function RecipeRevisions({
    revisions,
    currentRevisionId,
    onClose,
    onSelect,
}: RecipeRevisionsProps): ReactElement {
    const [authors, setAuthors] = useState<Map<number, User>>(new Map());

    useEffect(() => {
        const fetchAuthors = async () => {
            const authorIds = new Map<number, User>();
            const uniqueAuthorIds = Array.from(new Set(revisions.map(rev => rev.author_id)));
            const authorPromises = uniqueAuthorIds.map(getUserById);
            const users = await Promise.all(authorPromises);
            for (const user of users) {
                if (user) {
                    authorIds.set(user.id, user);
                }
            }

            setAuthors(authorIds);
        };
        fetchAuthors();
    }, [revisions]);

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center">
                <Typography variant="h6">Historik</Typography>
                <Box flexGrow="1" justifyContent="right" display="flex">
                    <IconButton onClick={onClose}>
                        <CloseIcon></CloseIcon>
                    </IconButton>
                </Box>
            </Stack>
            <List>
                {revisions.map(rev => {
                    const d = new Date(rev.created_timestamp * 1000);
                    const dateString = `${d.getFullYear()} ${getMonth(d)} ${d.getDate().toString().padStart(2, "0")}`;
                    const timeString = `${d.getHours().toString().padStart(2, "0")}:${d
                        .getMinutes()
                        .toString()
                        .padStart(2, "0")}`;
                    const formattedDate = `${dateString} — ${timeString}`;

                    return (
                        <ListItemButton
                            key={rev.id}
                            divider
                            onClick={() => onSelect?.(rev)}
                            selected={rev.id === currentRevisionId}
                        >
                            <ListItemText
                                disableTypography
                                primary={formattedDate}
                                secondary={<RecipeAuthor author={authors.get(rev.author_id)}></RecipeAuthor>}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </Paper>
    );
}
